import { Paginated } from '@/types';
import { InfiniteScroll, Link } from '@inertiajs/react';
import { PlaceList, SessionFlash, PlaceListItem, PlaceListBookmark } from '../types';
import { FirstUseCTAContainer } from './components/FirstUseCTAContainer';
import Icon from '@mdi/react';
import { mdiMapMarkerPlus } from '@mdi/js';
import { PlaceListLayout } from './components/PlaceListLayout';
import { Button } from '@/trailmix/Button';
import { MapLayout } from '@/layouts/MapLayout';
import { PlaceListItemDetail } from './components/PlaceListItemDetail';
import { Spinner } from '@chakra-ui/react';

function ListsShowEmptyState({ list, canUpdate }: { list: PlaceList; canUpdate: boolean }) {
    if (canUpdate) {
        return (
            <FirstUseCTAContainer className='mt-4'>
                <p>Start your next journey by adding to this list.</p>
                <Button
                    as={Link}
                    href={route('search.index', { fromList: list.slug })}
                    variant='success'
                    leftIcon={<Icon path={mdiMapMarkerPlus} size={0.8} />}
                >
                    Add a place
                </Button>
            </FirstUseCTAContainer>
        );
    }

    return (
        <div className='flex justify-center py-4'>
            <p className='text-gray-600'>No places found.</p>
        </div>
    );
}

function InfiniteScrollLoader() {
    return (
        <div className='my-16 flex justify-center'>
            <Spinner />
        </div>
    );
}

type ListsShowProps = {
    list: { data: PlaceList };
    placeListItems: Paginated<PlaceListItem>;
    placeListBookmark: PlaceListBookmark | null;
    flash: SessionFlash;
    can: {
        update_list: boolean;
        bookmark_list: boolean;
    };
};

export default function ListsShow({ list, placeListItems, placeListBookmark, can }: ListsShowProps) {

    return (
        <PlaceListLayout
            list={list.data}
            placeListBookmark={placeListBookmark}
            canUpdateList={can.update_list}
            canBookmarkList={can.bookmark_list}
        >
            {!list.data.total_place_count ? (
                // No entries in the list
                <ListsShowEmptyState list={list.data} canUpdate={can.update_list} />
            ) : !placeListItems.meta.total ? (
                // No entries for given filter
                <div className='mb-36 flex justify-center py-8'>
                    <p className='text-gray-600'>No places found.</p>
                </div>
            ) : (
                <InfiniteScroll data='placeListItems' as='div' buffer={250} loading={InfiniteScrollLoader} onlyNext>
                    <div className='divide-y-2'>
                        {placeListItems.data.map(item => (
                            <PlaceListItemDetail
                                key={item.id}
                                placeListItem={item}
                                visits={item.place.visits}
                                canUpdate={can.update_list}
                            />
                        ))}
                    </div>
                </InfiniteScroll>
            )}
        </PlaceListLayout>
    );
}

// eslint-disable-next-line react/no-children-prop
ListsShow.layout = (page: React.ReactNode) => <MapLayout children={page} />;
